import React from 'react'
import { useRef } from 'react'
// import { useTranslation } from 'react-i18next'
import { OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import styles from './EventEditor.module.scss'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import checkIcon from 'assets/images/menu-check.svg'
import SimpleColorButton from '../common/SimpleColorButton'
import classNames from 'classnames'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'

interface CalendarColor {
  _cdata: string
}

interface Calendar {
  url: string
  displayName: string
  owner: string
  calendarColor: CalendarColor
  isReadOnly: boolean
}

interface EditEventData {
  getCalendar: () => Calendar | null
  getColor: () => string
}

interface CalendarSelectorProps {
  editEventData: EditEventData
  calendars: Calendar[]
  onCalendarChange: (calendar: Calendar) => void
  onColorChange: (color: string) => void
  disabled: boolean
}

const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  editEventData,
  calendars,
  onCalendarChange,
  onColorChange,
  disabled,
}) => {
  // const { t } = useTranslation()
  const calendarButtonRef = useRef<HTMLButtonElement>(null)

  const getCalendarItems = (): OmMenuItem[] => {
    return (calendars ?? [])
      .filter((calendar) => !calendar.isReadOnly)
      .map((calendar) => ({
        key: calendar.url,
        label: (
          <div className={styles.menuItem}>
            <div className={styles.menuItemContent}>
              <div
                className={styles.colorDot}
                style={{ backgroundColor: calendar.calendarColor._cdata }}
              />
              <div className={styles.calendarInfo}>
                <div className={styles.calendarName}>{calendar.displayName}</div>
                {!isOrgSharedAccountEmail(calendar.owner) && (
                  <div className={styles.calendarOwner}>{calendar.owner}</div>
                )}
              </div>
            </div>
            {editEventData.getCalendar()?.url === calendar.url && (
              <img src={checkIcon} alt="Selected" className={styles.checkIcon} />
            )}
          </div>
        ),
        onClick: () => onCalendarChange(calendar),
      }))
  }

  const CalendarIcon: React.FC<{ color?: string }> = ({ color = '#666666' }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.titleIcon}
    >
      <path
        d="M15.4881 3.70239H4.36224C2.91954 3.70239 1.75 4.87193 1.75 6.31464V14.8282C1.75 16.2709 2.91954 17.4405 4.36224 17.4405H15.4881C16.9308 17.4405 18.1003 16.2709 18.1003 14.8282V6.31464C18.1003 4.87193 16.9308 3.70239 15.4881 3.70239Z"
        stroke={color}
        strokeWidth="1.5"
      />
      <path d="M2 7.28564H18" stroke={color} strokeWidth="1.5" />
      <path d="M13.7143 2.14282V4.99997" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M6.28571 2.14282V4.99997" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M5.95001 14.6C6.69559 14.6 7.30001 13.9956 7.30001 13.25C7.30001 12.5044 6.69559 11.9 5.95001 11.9C5.20442 11.9 4.60001 12.5044 4.60001 13.25C4.60001 13.9956 5.20442 14.6 5.95001 14.6Z"
        fill={color}
      />
    </svg>
  )

  return (
    <div className={styles.calendarSelector}>
      <div style={{ width: '20px', height: '20px' }}>
        <CalendarIcon color={editEventData.getColor()} />
      </div>
      {/* <img src={folderIcon} alt="Calendar" className={styles.titleIcon} /> */}
      <OmDropdownMenu
        menu={{ items: getCalendarItems() }}
        trigger={['click']}
        placement="bottomLeft"
        getPopupContainer={() => document.body}
        disabled={disabled}
        overlayClassName={classNames(styles.calendarDropdownOverlay, 'calendar-selector-dropdown')}
        onOpenChange={(open) => {
          if (open) {
            setTimeout(() => {
              const dropdown = document.querySelector('.calendar-selector-dropdown')
              if (dropdown && calendarButtonRef.current) {
                const tempWidth = calendarButtonRef.current.clientWidth
                const width = tempWidth < 240 ? 240 : tempWidth
                ;(dropdown as HTMLElement).style.width = `${width}px`
              }
            }, 100)
          }
        }}
      >
        <button
          className={`${styles.calendarButton} ${disabled ? styles.dimmed : ''}`}
          disabled={disabled}
          ref={calendarButtonRef}
        >
          <div className={styles.calendarDetails}>
            <div className={styles.calendarInfo}>
              <div className={styles.calendarName}>
                {editEventData.getCalendar()?.displayName || 'Loading...'}
              </div>
              {editEventData.getCalendar()?.owner &&
                !isOrgSharedAccountEmail(editEventData.getCalendar()?.owner) && (
                  <div className={styles.calendarOwner}>
                    <div className={styles.calendarOwnerEmail}>
                      {editEventData.getCalendar()?.owner}
                    </div>
                  </div>
                )}
            </div>
          </div>
          <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
        </button>
      </OmDropdownMenu>
      <SimpleColorButton
        color={editEventData.getColor()}
        onChange={onColorChange}
        appendColor={editEventData.getCalendar()?.calendarColor?._cdata}
      />
    </div>
  )
}

export default CalendarSelector
